"use client";

import { Component, ErrorInfo, ReactNode, useCallback } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

interface TransactionErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

interface TransactionErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onRetry?: () => void;
}

export class TransactionErrorBoundary extends Component<TransactionErrorBoundaryProps, TransactionErrorBoundaryState> {
  constructor(props: TransactionErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<TransactionErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Transaction Error Boundary caught an error:", error, errorInfo);

    this.setState({
      hasError: true,
      error,
      errorInfo,
      retryCount: this.state.retryCount
    });
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      retryCount: prevState.retryCount + 1
    }));

    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      const maxRetriesExceeded = this.state.retryCount >= 3;

      return this.props.fallback || (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                Erro na Transação
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="font-medium">Ocorreu um erro inesperado:</div>
                  <div className="text-sm mt-2">
                    {this.state.error?.message || "Erro desconhecido ao processar transação"}
                  </div>
                  {this.state.errorInfo && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <details className="cursor-pointer">
                        <summary className="font-medium">Detalhes técnicos</summary>
                        <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                          {JSON.stringify({
                            componentStack: this.state.errorInfo.componentStack,
                            error: this.state.error?.stack
                          }, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </AlertDescription>
              </Alert>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={this.handleRetry}
                  disabled={maxRetriesExceeded}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {maxRetriesExceeded ? "Número máximo de tentativas excedido" : "Tentar Novamente"}
                </Button>

                {maxRetriesExceeded && (
                  <Button
                    onClick={() => window.location.reload()}
                    variant="default"
                    className="w-full sm:w-auto"
                  >
                    Recarregar Página
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook for transaction error handling
export function useTransactionErrorHandler() {
  const handleTransactionError = useCallback((error: Error, context: string) => {
    console.error(`Transaction error in ${context}:`, error);

    // Log to error tracking service (if available)
    if (window.gtag) {
      window.gtag('event', 'transaction_error', {
        error_context: context,
        error_message: error.message,
        error_stack: error.stack
      });
    }

    // Show user-friendly error message
    const userMessage = getUserFriendlyErrorMessage(error, context);
    if (userMessage) {
      toast.error(userMessage);
    }
  }, []);

  return { handleTransactionError };
}

function getUserFriendlyErrorMessage(error: Error, context: string): string {
  // Network errors
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return "Falha na conexão. Verifique sua internet e tente novamente.";
  }

  // Authentication/Session errors
  if (error.message.includes('JWT') || error.message.includes('auth') || error.message.includes('unauthorized')) {
    return "Sessão expirada. Faça login novamente.";
  }

  // Permission errors
  if (error.message.includes('permission') || error.message.includes('forbidden')) {
    return "Você não tem permissão para realizar esta operação.";
  }

  // Validation errors
  if (error.message.includes('Required') || error.message.includes('validation')) {
    return "Campos obrigatórios estão faltando. Verifique o preenchimento do formulário.";
  }

  // Server errors
  if (error.message.includes('500') || error.message.includes('internal server')) {
    return "Erro interno do servidor. Tente novamente em alguns minutos.";
  }

  // Database constraint errors
  if (error.message.includes('constraint') || error.message.includes('duplicate')) {
    return "Dados duplicados ou inválidos. Verifique as informações e tente novamente.";
  }

  // Rate limiting
  if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
    return "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
  }

  // File upload errors
  if (error.message.includes('upload') || error.message.includes('file') || error.message.includes('storage')) {
    return "Erro ao processar arquivos. Verifique o tamanho e formato dos anexos.";
  }

  // Default error with context
  return `Ocorreu um erro ao ${context}. Tente novamente ou contate o suporte se o problema persistir.`;
}
